import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Loader } from '@els/els-react--loader';
import { commaFormatNumber } from '@Utils/String';
import RsSlidable from '@Pages/RetroSynthesis/components/RsSlidable/RsSlidable';
import { CONTROLS_PLACEMENTS } from '@Pages/RetroSynthesis/components/RsSlidable/constants';
import { LEYDEN_TAB_GROUP_CLASSES } from '@Containers/ResultToolbar/DatabaseSwitcher/LeydenClasses';

import './DatabaseSwitcherTabs.scss';

function DatabaseSwitcherTabGroup({
  className,
  currentDatabase,
  isDbSwitcherVisible,
  availableDatabases,
  selectDatabase,
  shouldRenderAriaControls = true,
}) {
  const tabsRenderer = (item) => {
    // Extract nested ternary into a named variable for readability
    let countContent;

    if (item.isLoading) {
      countContent = <Loader isSmall isBlocking={false} className="database-switcher-new__tab-loader" />;
    } else if (item.canceled) {
      countContent = 'canceled';
    } else {
      countContent = commaFormatNumber(item.count);
    }

    const isActive = item.databaseId === currentDatabase.databaseId;
    const controlledPanelAttrs = shouldRenderAriaControls ? { 'aria-controls': `results-tabpanel-${item.context}-${item.databaseId}` } : {};

    return (
      <button
        disabled={!item.count}
        type="button"
        id={`databases-tab-${item.context}-${item.databaseId}`}
        role="tab"
        aria-selected={isActive}
        {...controlledPanelAttrs}
        className={cn(LEYDEN_TAB_GROUP_CLASSES.tabGroupItem, LEYDEN_TAB_GROUP_CLASSES.tabGroupText, {
          [LEYDEN_TAB_GROUP_CLASSES.tabGroupItemActive]: isActive,
          [LEYDEN_TAB_GROUP_CLASSES.tabGroupItemDisabled]: !item.count,
        })}
        onClick={() => {
          selectDatabase(item);
        }}
        data-e2e="database-switcher-tab">
        <span className="database-switcher-new__tab-content">
          {item.title}
          {countContent}
        </span>
      </button>
    );
  };

  const currentTabIndex = useMemo(
    () => currentDatabase && availableDatabases.findIndex(({ databaseId }) => databaseId === currentDatabase.databaseId),
    [availableDatabases, currentDatabase]
  );

  const contentAttrs = {
    role: 'tablist',
    'aria-label': 'tabs',
  };
  const itemAttrs = () => ({
    role: 'presentation',
  });

  return isDbSwitcherVisible ? (
    <div className={cn('database-switcher-new', className)}>
      <RsSlidable
        className="database-switcher-new__slidable rx-flex-1"
        containerClassName={cn('database-switcher-new__list-container', LEYDEN_TAB_GROUP_CLASSES.tabGroup)}
        contentClassName={cn(LEYDEN_TAB_GROUP_CLASSES.tabGroup, LEYDEN_TAB_GROUP_CLASSES.tabGroupDivider)}
        contentAttrs={contentAttrs}
        itemAttrs={itemAttrs}
        items={availableDatabases}
        renderItem={tabsRenderer}
        visibleItemIndex={currentTabIndex}
        controlsPlacement={CONTROLS_PLACEMENTS.bothSides}
      />
    </div>
  ) : null;
}

DatabaseSwitcherTabGroup.propTypes = {
  className: PropTypes.string,
  currentDatabase: PropTypes.object,
  isDbSwitcherVisible: PropTypes.bool,
  selectDatabase: PropTypes.func,
  availableDatabases: PropTypes.array,
  shouldRenderAriaControls: PropTypes.bool,
};

export default DatabaseSwitcherTabGroup;
